import { ShipTierColors, ShipTierNames } from "@voidex/contracts-ts";
import { useState } from "react";
import { formatEther } from "viem";
import { FarmOutcomeColors, FarmOutcomeNames } from "../../hooks";
import type { ResolvedMission } from "../../hooks";
import { HudDivider } from "../shared/HudDivider";
import { ShipIcon } from "../shared/ShipIcon";
import { ClaimAnimationModal } from "./ClaimAnimationModal";

interface MissionHistoryProps {
	missions: ResolvedMission[];
	isLoading: boolean;
}

function relativeTime(unixSeconds: bigint): string {
	const diff = Math.floor(Date.now() / 1000) - Number(unixSeconds);
	if (diff < 60) return "just now";
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
	return `${Math.floor(diff / 86400)}d ago`;
}

export function MissionHistory({ missions, isLoading }: MissionHistoryProps) {
	const [replayMission, setReplayMission] = useState<ResolvedMission | null>(null);

	if (isLoading) {
		return (
			<div className="mission-history">
				<HudDivider label="MISSION LOG" />
				<div className="mission-history__loading">
					<div className="loading-spinner" />
					<span>Loading history...</span>
				</div>
			</div>
		);
	}

	if (missions.length === 0) return null;

	return (
		<div className="mission-history">
			<HudDivider label="MISSION LOG" count={missions.length} />
			<div className="mission-log">
				{missions.map((mission) => {
					const tierName = ShipTierNames[mission.shipTier];
					const tierColor = ShipTierColors[mission.shipTier];
					const outcomeName = FarmOutcomeNames[mission.outcome];
					const outcomeColor = FarmOutcomeColors[mission.outcome];
					const formattedPoints = Number.parseFloat(formatEther(mission.pointsEarned)).toFixed(2);

					return (
						<button
							type="button"
							key={mission.sessionId.toString()}
							className={`mission-log__entry ${mission.shipDestroyed ? "mission-log__entry--destroyed" : ""}`}
							onClick={() => setReplayMission(mission)}
						>
							<ShipIcon tier={mission.shipTier} size="sm" />
							<span className="mission-log__id">#{mission.shipTokenId.toString()}</span>
							<span className="mission-log__tier" style={{ color: tierColor }}>
								{tierName}
							</span>
							<span className="mission-log__outcome" style={{ color: outcomeColor }}>
								{outcomeName}
							</span>
							<span className="mission-log__points">
								{mission.shipDestroyed ? "DESTROYED" : `+${formattedPoints}`}
							</span>
							<span className="mission-log__time">{relativeTime(mission.resolvedTimestamp)}</span>
						</button>
					);
				})}
			</div>

			{replayMission && (
				<ClaimAnimationModal
					sessionId={replayMission.sessionId}
					zone={0}
					shipTier={replayMission.shipTier}
					mission={replayMission}
					onClose={() => setReplayMission(null)}
				/>
			)}
		</div>
	);
}
